/**
 * Print readiness tracker.
 *
 * The print routes (`/print-reports/:id/:month` and `/print-reports/:id`) are
 * rendered by an external Puppeteer service that converts the page to a PDF.
 *
 * amCharts / Sankey charts render asynchronously after their data is fetched.
 * If Puppeteer prints before every chart has finished drawing, charts come out
 * empty and whole pages can look blank. To make printing deterministic we keep a
 * counter of outstanding async render work and expose a single boolean flag on
 * `window` that the Puppeteer service can wait for:
 *
 *   await page.waitForFunction('window.__VOLTA_REPORT_READY__ === true')
 *
 * The flag is only meaningful on the print routes; on normal app routes it is
 * left untouched so it never blocks interactive usage.
 */

const FLAG = '__VOLTA_REPORT_READY__'

// Number of charts/sections that have started but not yet finished rendering.
let pending = 0
// Set to true once at least one render has been registered, so we don't report
// "ready" before any chart has even started mounting.
let started = false
let settleTimer = null

function isPrintRoute() {
    if (typeof window === 'undefined') return false
    return window.location && window.location.hash
        ? window.location.hash.indexOf('/print-reports/') !== -1
        : false
}

function setFlag(value) {
    if (typeof window === 'undefined') return
    window[FLAG] = value
}

function refresh() {
    if (!isPrintRoute()) return

    if (pending > 0 || !started) {
        setFlag(false)
        return
    }

    // Debounce: wait a short, quiet period with no new render work before
    // declaring the report ready. This lets amCharts finish its final layout
    // pass and any follow-up `$nextTick` renders settle.
    if (settleTimer) {
        clearTimeout(settleTimer)
    }
    settleTimer = setTimeout(() => {
        if (pending === 0 && started) {
            setFlag(true)
        }
    }, 600)
}

/** Register that an async chart/section render has started. */
export function startRender() {
    started = true
    pending += 1
    setFlag(false)
}

/** Register that a previously started render has finished. */
export function finishRender() {
    pending = Math.max(0, pending - 1)
    refresh()
}

/**
 * Mark that report content has mounted. Call this once the report pages are in
 * the DOM so that reports without any charts (only tables / text / images)
 * still flip the ready flag instead of hanging the Puppeteer service forever.
 */
export function markStarted() {
    started = true
    refresh()
}

/** Reset tracking (call when a fresh report load begins). */
export function resetReady() {
    pending = 0
    started = false
    if (settleTimer) {
        clearTimeout(settleTimer)
        settleTimer = null
    }
    setFlag(false)
}

export default {
    startRender,
    finishRender,
    resetReady,
}



// WEBPACK FOOTER //
// ./src/helpers/printReady.js